// Luas Forecasts XML API (https://luasforecasts.rpa.ie) — stops, live forecast, status/travel update, map features.
// Always call with ver=2&encrypt=false per the API documentation.

export const LUAS_FORECASTS_BASE_URL = "https://luasforecasts.rpa.ie/xml/get.ashx"

export type LuasStop = {
  abrev: string
  name: string
  pronunciation: string
  lat: number
  long: number
  isParkRide: boolean
  isCycleRide: boolean
  inboundStatusMessage: string
  inboundForecastsEnabled: boolean
  inboundOperatingNormally: boolean
  outboundStatusMessage: string
  outboundForecastsEnabled: boolean
  outboundOperatingNormally: boolean
}

export type LuasLine = {
  name: string
  statusMessage: string
  stops: LuasStop[]
}

export type LuasTram = {
  dueMins: string
  destination: string
}

export type LuasDirection = {
  name: string
  statusMessage: string
  forecastsEnabled: boolean
  operatingNormally: boolean
  trams: LuasTram[]
}

export type LuasForecast = {
  createdAt: string
  stop: string
  stopAbrev: string
  message: string
  directions: LuasDirection[]
}

export type LuasStatusStop = {
  abrev: string
  name: string
  pronunciation: string
  lat: number
  long: number
  isParkRide: boolean
  isCycleRide: boolean
  inboundStatusMessage: string
  inboundForecastsEnabled: boolean
  inboundOperatingNormally: boolean
  outboundStatusMessage: string
  outboundForecastsEnabled: boolean
  outboundOperatingNormally: boolean
}

export type LuasStatusLine = {
  name: string
  operatingNormally: boolean
  linewideMessage: string
  stops: LuasStatusStop[]
}

export type LuasStatus = {
  createdAt: string
  lines: LuasStatusLine[]
  travelUpdateHtml: string
  travelUpdateLightHomepage: boolean
}

export type LuasFeature = {
  name: string
  lat: number
  long: number
  type: string
}

function toBoolean(value: string | null | undefined): boolean {
  return (value ?? "").toLowerCase() === "true"
}

function toNumber(value: string | null | undefined): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

// The status endpoint's <travelUpdate> body is HTML, XML-escaped by the API.
function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
}

function parseXml(xml: string): Document {
  return new DOMParser().parseFromString(xml, "application/xml")
}

async function fetchXml(action: string, params: Record<string, string> = {}): Promise<string> {
  const query = new URLSearchParams({ action, ver: "2", encrypt: "false", ...params })
  const response = await fetch(`${LUAS_FORECASTS_BASE_URL}?${query.toString()}`)
  if (!response.ok) throw new Error(`Luas API HTTP ${response.status} (action=${action})`)
  return response.text()
}

export function parseStopsXml(xml: string): LuasLine[] {
  const doc = parseXml(xml)
  return Array.from(doc.querySelectorAll("line")).map(line => ({
    name: line.getAttribute("name") ?? "",
    statusMessage: line.getAttribute("statusMessage") ?? "",
    stops: Array.from(line.querySelectorAll("stop")).map(stop => ({
      abrev: stop.getAttribute("abrev") ?? "",
      name: stop.textContent?.trim() ?? "",
      pronunciation: stop.getAttribute("pronunciation") ?? "",
      lat: toNumber(stop.getAttribute("lat")),
      long: toNumber(stop.getAttribute("long")),
      isParkRide: stop.getAttribute("isParkRide") === "1",
      isCycleRide: stop.getAttribute("isCycleRide") === "1",
      inboundStatusMessage: stop.getAttribute("inboundStatusMessage") ?? "",
      inboundForecastsEnabled: toBoolean(stop.getAttribute("inboundForecastsEnabled")),
      inboundOperatingNormally: toBoolean(stop.getAttribute("inboundOperatingNormally")),
      outboundStatusMessage: stop.getAttribute("outboundStatusMessage") ?? "",
      outboundForecastsEnabled: toBoolean(stop.getAttribute("outboundForecastsEnabled")),
      outboundOperatingNormally: toBoolean(stop.getAttribute("outboundOperatingNormally")),
    })),
  }))
}

// Per the API doc, a placeholder row ("No Inbound Service", "No trams forecast", etc.) has no destination/dueMins.
export function isPlaceholderTram(tram: LuasTram): boolean {
  return !tram.destination || !tram.dueMins
}

export function parseForecastXml(xml: string): LuasForecast {
  const doc = parseXml(xml)
  const stopInfo = doc.querySelector("stopInfo")

  return {
    createdAt: stopInfo?.getAttribute("created") ?? "",
    stop: stopInfo?.getAttribute("stop") ?? "",
    stopAbrev: stopInfo?.getAttribute("stopAbv") ?? "",
    message: doc.querySelector("message")?.textContent?.trim() ?? "",
    directions: Array.from(doc.querySelectorAll("direction")).map(direction => ({
      name: direction.getAttribute("name") ?? "",
      statusMessage: direction.getAttribute("statusMessage") ?? "",
      forecastsEnabled: toBoolean(direction.getAttribute("forecastsEnabled")),
      operatingNormally: toBoolean(direction.getAttribute("operatingNormally")),
      trams: Array.from(direction.querySelectorAll("tram"))
        .map(tram => ({
          dueMins: tram.getAttribute("dueMins") ?? "",
          destination: tram.getAttribute("destination") ?? "",
        }))
        .filter(tram => !isPlaceholderTram(tram)),
    })),
  }
}

export function parseStatusXml(xml: string): LuasStatus {
  const doc = parseXml(xml)
  const travelUpdate = doc.querySelector("travelUpdate")

  return {
    createdAt: doc.querySelector("status")?.getAttribute("created") ?? "",
    lines: Array.from(doc.querySelectorAll("line")).map(line => ({
      name: line.getAttribute("name") ?? "",
      operatingNormally: toBoolean(line.getAttribute("operatingNormally")),
      linewideMessage: line.getAttribute("linewideMessage") ?? "",
      stops: Array.from(line.querySelectorAll("stop")).map(stop => ({
        abrev: stop.getAttribute("abrev") ?? "",
        name: stop.getAttribute("name") ?? "",
        pronunciation: stop.getAttribute("pronunciation") ?? "",
        lat: toNumber(stop.getAttribute("lat")),
        long: toNumber(stop.getAttribute("long")),
        isParkRide: stop.getAttribute("isParkRide") === "1",
        isCycleRide: stop.getAttribute("isCycleRide") === "1",
        inboundStatusMessage: stop.querySelector("inbound")?.textContent?.trim() ?? "",
        inboundForecastsEnabled: toBoolean(stop.querySelector("inbound")?.getAttribute("forecasts_enabled")),
        inboundOperatingNormally: toBoolean(stop.querySelector("inbound")?.getAttribute("operating_normally")),
        outboundStatusMessage: stop.querySelector("outbound")?.textContent?.trim() ?? "",
        outboundForecastsEnabled: toBoolean(stop.querySelector("outbound")?.getAttribute("forecasts_enabled")),
        outboundOperatingNormally: toBoolean(stop.querySelector("outbound")?.getAttribute("operating_normally")),
      })),
    })),
    travelUpdateHtml: decodeHtmlEntities(travelUpdate?.textContent?.trim() ?? ""),
    travelUpdateLightHomepage: toBoolean(travelUpdate?.getAttribute("light_homepage")),
  }
}

export function parseFeaturesXml(xml: string): LuasFeature[] {
  const doc = parseXml(xml)
  return Array.from(doc.querySelectorAll("feature")).map(feature => ({
    name: feature.getAttribute("name") ?? "",
    lat: toNumber(feature.getAttribute("lat")),
    long: toNumber(feature.getAttribute("long")),
    type: feature.getAttribute("type") ?? "",
  }))
}

export async function getStops(): Promise<LuasLine[]> {
  return parseStopsXml(await fetchXml("stops"))
}

export async function getForecast(stopAbrev: string): Promise<LuasForecast> {
  return parseForecastXml(await fetchXml("forecast", { stop: stopAbrev.toUpperCase() }))
}

/** filterStopName narrows the response to a single stop by display name (not the 3-letter abrev). */
export async function getStatus(filterStopName?: string): Promise<LuasStatus> {
  return parseStatusXml(await fetchXml("status", filterStopName ? { filter: filterStopName } : {}))
}

export async function getFeatures(): Promise<LuasFeature[]> {
  return parseFeaturesXml(await fetchXml("features"))
}
